interface ApiValidationIssue {
  path?: string;
  message?: string;
  type?: string;
}

interface ApiErrorBody {
  issues?: ApiValidationIssue[];
  details?: {
    issues?: ApiValidationIssue[];
  } | null;
}

const DEFAULT_VALIDATION_MESSAGE = 'Some information is invalid. Please review and try again.';
const VALIDATION_MESSAGE_BY_PATH: Record<string, string> = {
  email: 'Please enter a valid email address.',
  customerEmail: 'Please enter a valid email address.',
  address: 'Please enter a valid address.',
  changeLogs: 'Unable to save company change history. Please refresh and try again.'
};
const VALIDATION_MESSAGE_BY_TYPE: Record<string, string> = {
  'any.required': 'Some required information is missing. Please review and try again.',
  'string.email': 'Please enter a valid email address.',
  'string.empty': 'Please enter a valid email address.',
  'string.pattern.base': 'Some information has an invalid format. Please review and try again.',
  'alternatives.match': 'Some information has an invalid format. Please review and try again.',
  'any.only': 'Some selected values are invalid. Please review and try again.'
};

const toTrimmedString = (value: unknown) => String(value ?? '').trim();

const getValidationIssues = (data: unknown): ApiValidationIssue[] => {
  if (!data || typeof data !== 'object') return [];
  const payload = data as ApiErrorBody;
  if (Array.isArray(payload.issues)) return payload.issues;
  if (Array.isArray(payload.details?.issues)) return payload.details?.issues || [];
  return [];
};

const getFriendlyValidationMessage = (issue?: ApiValidationIssue) => {
  if (!issue) return DEFAULT_VALIDATION_MESSAGE;

  const pathMessage = VALIDATION_MESSAGE_BY_PATH[toTrimmedString(issue.path)];
  if (pathMessage) return pathMessage;

  const typeMessage = VALIDATION_MESSAGE_BY_TYPE[toTrimmedString(issue.type)];
  if (typeMessage) return typeMessage;

  return DEFAULT_VALIDATION_MESSAGE;
};

const getServerErrorMessage = (data: unknown) => {
  if (!data || typeof data !== 'object') return '';
  const { error } = data as Record<string, unknown>;
  return typeof error === 'string' ? toTrimmedString(error) : '';
};

export const buildApiErrorMessage = (status: number, data: unknown, fallback: string) => {
  const issues = getValidationIssues(data);
  if (issues.length > 0) {
    return getFriendlyValidationMessage(issues[0]);
  }

  if (status === 401) return 'Unauthorized. Please sign in again.';
  if (status === 403) return 'Access denied. Please sign in again.';

  // For 400/404/409 the server sends a specific, user-facing message in `error`.
  // Prefer it so actionable conflicts (e.g. "this email is already a team member")
  // aren't flattened into a generic "please refresh" message.
  if (status === 400 || status === 404 || status === 409) {
    const serverMessage = getServerErrorMessage(data);
    if (serverMessage) return serverMessage;
  }

  if (status === 404) return 'The requested record was not found.';
  if (status === 409)
    return 'This action conflicts with current data. Please refresh and try again.';
  if (status >= 500) return 'Something went wrong. Please try again shortly.';

  const normalizedFallback = toTrimmedString(fallback);
  if (normalizedFallback) return normalizedFallback;
  return 'Unable to process your request. Please try again.';
};
